import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getFormValidation } from "src/Shared/Components";
import GridFormControl from "src/Shared/Components/GridFormControl/GridFormControl";
import ModalContent from "src/Shared/Components/ModalContent/ModalContent";
import { EDITOR_TYPES } from "src/Shared/Enums";
import { useModal, useStore } from "src/Shared/Hooks";
import { IResult, ISelectOption } from "src/Shared/Interfaces";
import { IGridFormControlProps } from "src/Shared/Interfaces/IGridFormControlProps.interface";
import { SLAStatus } from "../../Enums";
import { fields } from "./fields";
import ViewInputControl from "src/Shared/Components/ViewInputControl/ViewInputControl";
import { useStyles } from "./CompanySLAInfo.styles";
import { AxiosError } from "axios";
import { finalize } from "rxjs";
import useDebounce from "src/Shared/Hooks/UseDebounce.hook";
import { ToastService } from "src/Shared/Services";
import { ICommentFilter } from "../../Interfaces";
import CommentService from "../../Services/Comment.service";
import AddComments, { AddCommentsRef } from "../AddComments/AddComments";
import { IUserDTO } from "../../Interfaces/IUserDTO";
import { observer } from "mobx-react";
import CompanyService from "../../Services/Company.service";
import { ICompanySLAInfoDTO } from "../../Interfaces/ICompanySLAInfoDTO";

type Props = {
  companyRowID: string;
  primaryCompanyRelationshipManager?: IUserDTO;
};
const CompanySLAInfo: React.FC<Props> = ({
  companyRowID,
  primaryCompanyRelationshipManager,
}) => {
  const [filteredSlaStatus, setSlaStatus] = useState<ISelectOption[]>();

  const classes = useStyles();

  const [saveLoading, setSaveLoading] = useState<boolean>(false);

  const { close } = useModal();
  const slaForm = useMemo(() => getFormValidation(fields), []);

  const getField = (key: string) => slaForm.$(key);
  const {
    rootStore: {
      commonStore: { slaStatus },
      companyStore: { companySlaInfo, getCompanySlaInfo, getCompany },
      addCommentStore: { btnLoading },
    },
  } = useStore();

  // Initial Load
  useEffect(() => {
    getCompanySlaInfo(companyRowID);
  }, []);

  useEffect(() => {
    slaForm.set(companySlaInfo);
  }, [companySlaInfo]);

  useEffect(() => {
    setSlaStatus(
      slaStatus.filter(
        (x) =>
          getField("isCurrentSLAAvaliable")?.value === true ||
          getField("isCurrentSLAAvaliable")?.value === "true" ||
          x.value != SLAStatus.CurrentSLA,
      ),
    );
  }, [getField("isCurrentSLAAvaliable").value]);

  const onSlaStatusChange = (val: SLAStatus) => {
    if (val == SLAStatus.ClientRefused) {
      getField("slaReminderDate").set("rules", "required");
      getField("slaRecipient").set("rules", "required");

      getField("slaRecipient").set("value", primaryCompanyRelationshipManager);
    } else {
      // Remove required rules
      getField("slaReminderDate").set("rules", "");
      getField("slaRecipient").set("rules", "");

      // Clear recipient value
      getField("slaRecipient").set("value", null);
    }
  };

  const onSubmitForm = () => {
    if (!slaForm.isValid) {
      slaForm.validate();
      slaForm.showErrors();
      return;
    }
    let request: ICompanySLAInfoDTO = {
      companyID: companySlaInfo.companyID,
      ...slaForm.values(),
    };
    setSaveLoading(true);
    CompanyService.addOrUpdateCompanySLAInfo(request)
      .pipe(
        finalize(() => {
          setSaveLoading(true);
        }),
      )
      .subscribe({
        next: (data: IResult<string>) => {
          ToastService.success(data.message);
          getCompany(companyRowID);
          close();
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  };

  const slaGridProps: IGridFormControlProps = {
    row: 1,
    col: 7,
    spacing: 4,
    itemResponsive: {
      lg: 12,
      md: 12,
      sm: 12,
    },
    inputControls: [
      {
        field: getField("isCurrentSLAAvaliable"),
        fieldKey: "isCurrentSLAAvaliable",
        type: EDITOR_TYPES.RADIO_BUTTON,
        options: [
          {
            label: "Yes",
            value: true,
          },
          {
            label: "No",
            value: false,
          },
        ],
      },
      {
        field: getField("isHistoricSLAAvaliable"),
        fieldKey: "isHistoricSLAAvaliable",
        type: EDITOR_TYPES.RADIO_BUTTON,
        options: [
          {
            label: "Yes",
            value: "Yes",
          },
          {
            label: "No",
            value: "No",
          },
          {
            label: "Unknown",
            value: "Unknown",
          },
        ],
        hidden: !(
          getField("isCurrentSLAAvaliable").value !== undefined &&
          (getField("isCurrentSLAAvaliable").value === false ||
            getField("isCurrentSLAAvaliable").value === "false")
        ),
      },
      {
        field: getField("slaExpiryDate"),
        fieldKey: "slaExpiryDate",
        type: EDITOR_TYPES.DATE,
      },
      {
        field: getField("slaStatusID"),
        fieldKey: "slaStatusID",
        type: EDITOR_TYPES.DROPDOWN,
        options: filteredSlaStatus,
        onValueChange(val, _) {
          onSlaStatusChange(val);
        },
      },
      {
        field: getField("slaReminderDate"),
        fieldKey: "slaReminderDate",
        type: EDITOR_TYPES.DATE,
        hidden: !(getField("slaStatusID").value == SLAStatus.ClientRefused),
      },
      {
        field: getField("slaRecipient"),
        fieldKey: "slaRecipient",
        type: EDITOR_TYPES.AUTO_COMPLETE_ASYNC,
        hidden: !(getField("slaStatusID").value == SLAStatus.ClientRefused),
      },
    ],
    form: slaForm,
  };

  // Comment Section
  const formRef = useRef<AddCommentsRef>(null);
  const onCommentSubmit = () => {
    formRef.current.submitForm();
  };
  return (
    <>
      <ModalContent
        buttonText="Save"
        title="SLA Information"
        onSaveButtonClick={() => onSubmitForm()}
        btnLoading={saveLoading}
      >
        <Box>
          <Box sx={{ mt: 2 }}>
            <GridFormControl {...slaGridProps} />

            <AddComments
              ref={formRef}
              companyRowID={companyRowID}
              isSlaRelated={true}
            />
          </Box>
          <Box>
            <Box className={classes.submitButtonBox}>
              <Button
                variant="contained"
                onClick={onCommentSubmit}
                startIcon={btnLoading && <CircularProgress size={20} />}
                disabled={btnLoading}
              >
                Add Comment
              </Button>
            </Box>
          </Box>
        </Box>
      </ModalContent>
    </>
  );
};

export default observer(CompanySLAInfo);
